/* eslint-disable max-lines -- session, patient, and grouped routine history share one selector */
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2Bold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, LoaderCircle } from 'lucide-react';
import { useState, type ComponentProps, type ReactElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { ProjectListItem } from '../../../contracts/projects.types';
import { useProjectsList } from '../../../data/projects/hooks';

const SESSION_PROJECTS_FETCH_LIMIT = 200;

interface EvidenceChatRow {
  id: string;
  title: string;
  updated_at: string;
}

const EMPTY_EVIDENCE_CHATS: readonly EvidenceChatRow[] = [];

/** A routine run in this session's history: the fields the menu reads. */
export interface SessionWorkRoutineRun {
  project_id: string;
  source_workflow_id: string | null;
  title: string | null;
  created_at: string | null;
}

/** One routine's runs this visit, newest first. The routines domain groups them. */
export interface SessionWorkRoutineRunGroup {
  key: string;
  runs: readonly SessionWorkRoutineRun[];
}

/** A patient profile, as the menu names it. */
export interface SessionWorkPatientProfile {
  id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  demographic_string?: string;
}

/**
 * What the app supplies: this session's routine runs grouped per routine, the
 * routines (for their names) and the team's patient profiles. Each is called
 * unconditionally wherever the history is read — the open menu and the hover
 * prefetch. Module-level, so their identities are stable.
 */
export interface SessionWorkProjectSelectorHost {
  useSessionRoutineRunGroups: (sessionId: string) => {
    groups: readonly SessionWorkRoutineRunGroup[];
    isLoading: boolean;
  };
  useRoutines: () => {
    data?: { workflows: readonly { workflow_id: string; name: string }[] };
  };
  usePatientProfiles: () => { data?: SessionWorkPatientProfile[] };
}

export interface SessionWorkProjectSelectorProps {
  sessionId: string;
  /** Host session patient, when linked — lists A-associated chats too. */
  associatedPatientProfileId?: string | null;
  currentProjectId: string | null;
  currentProjectTitle?: string;
  onSelectProject: (projectId: string) => void;
  /** This session's classic Evidence chats; picking one shows it in the panel. */
  evidenceChats?: readonly EvidenceChatRow[];
  currentEvidenceChatId?: string | null;
  onSelectEvidenceChat?: (chatId: string) => void;
  /** Replaces the title-button trigger; rendered through `DropdownMenuTrigger asChild`. */
  trigger?: ReactElement;
  contentProps?: Pick<
    ComponentProps<typeof DropdownMenuContent>,
    'side' | 'align' | 'alignOffset' | 'className'
  >;
  onOpenChange?: (open: boolean) => void;
}

function isWritableInSession(
  project: ProjectListItem,
  sessionId: string,
  patientProfileId: string | null | undefined
): boolean {
  const scope = project.data_scope;
  return (
    (scope?.kind === 'session' && scope.session_id === sessionId) ||
    (scope?.kind === 'patient' && scope.patient_profile_id === patientProfileId)
  );
}

/** Session history ignores the list API's saved-first order. */
function byUpdatedAtDesc(a: ProjectListItem, b: ProjectListItem): number {
  const aTime = Date.parse(a.updated_at);
  const bTime = Date.parse(b.updated_at);
  const aValid = Number.isFinite(aTime);
  const bValid = Number.isFinite(bTime);
  if (!aValid && !bValid) return 0;
  if (!aValid) return 1;
  if (!bValid) return -1;
  return bTime - aTime;
}

const HistoryDate = ({ value }: { value: string }) => {
  const intl = useIntl();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  let label: string;
  if (sameDay) {
    label = intl.formatMessage({ defaultMessage: 'Today', id: 'zWgbGgjUUg' });
  } else {
    const daysAgo = Math.max(
      1,
      Math.round((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
    );
    label =
      daysAgo < 7
        ? intl.formatRelativeTime(-daysAgo, 'day')
        : daysAgo < 14
          ? intl.formatRelativeTime(-1, 'week')
          : intl.formatDate(date, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
  }

  return (
    <TypographyCaption className="shrink-0 text-text-secondary">
      {label}
    </TypographyCaption>
  );
};

function patientLabel(
  profile:
    | {
        first_name?: string | null;
        last_name?: string | null;
        demographic_string?: string;
      }
    | undefined
): string {
  if (!profile) return 'Unknown patient';
  const first = profile.first_name?.trim();
  const last = profile.last_name?.trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return profile.demographic_string?.split(',')[0]?.trim() || 'Unknown patient';
}

interface SessionWorkHistoryArgs {
  sessionId: string;
  associatedPatientProfileId?: string | null;
  host: SessionWorkProjectSelectorHost;
}

const useSessionWorkHistoryQueries = ({
  sessionId,
  associatedPatientProfileId,
  host,
}: SessionWorkHistoryArgs) => {
  const patientProfiles = host.usePatientProfiles();
  const projects = useProjectsList({
    session_id: sessionId,
    ...(associatedPatientProfileId
      ? { associated_patient_profile_id: associatedPatientProfileId }
      : {}),
    limit: SESSION_PROJECTS_FETCH_LIMIT,
  });
  const routineRuns = host.useSessionRoutineRunGroups(sessionId);
  const workflows = host.useRoutines();
  return { patientProfiles, projects, routineRuns, workflows };
};

/** Warms the history cache on trigger intent so the menu opens with data. */
const SessionWorkHistoryPrefetch = (props: SessionWorkHistoryArgs) => {
  useSessionWorkHistoryQueries(props);
  return null;
};

interface SessionWorkProjectSelectorContentProps extends SessionWorkHistoryArgs {
  currentProjectId: string | null;
  onSelectProject: (projectId: string) => void;
  evidenceChats: readonly EvidenceChatRow[];
  currentEvidenceChatId: string | null;
  onSelectEvidenceChat?: (chatId: string) => void;
}

const SessionWorkProjectSelectorContent = ({
  sessionId,
  associatedPatientProfileId,
  host,
  currentProjectId,
  onSelectProject,
  evidenceChats,
  currentEvidenceChatId,
  onSelectEvidenceChat,
}: SessionWorkProjectSelectorContentProps) => {
  const intl = useIntl();
  const {
    patientProfiles: { data: patientProfiles },
    projects: { data, isLoading },
    routineRuns: { groups: sessionRoutineGroups, isLoading: isRoutineRunsLoading },
    workflows: { data: workflowsData },
  } = useSessionWorkHistoryQueries({ sessionId, associatedPatientProfileId, host });
  const projects = data?.items ?? [];
  const projectIds = new Set(projects.map((project) => project.project_id));
  const workflowNameById = new Map(
    (workflowsData?.workflows ?? []).map((workflow) => [
      workflow.workflow_id,
      workflow.name,
    ])
  );
  const routineGroups = sessionRoutineGroups.filter(
    (group) =>
      group.runs.some((run) => Boolean(run.project_id)) &&
      !group.runs.some((run) => projectIds.has(run.project_id))
  );
  const hasProjects =
    projects.length > 0 || routineGroups.length > 0 || evidenceChats.length > 0;
  const isHistoryLoading = isLoading || isRoutineRunsLoading;
  const profiles = Array.isArray(patientProfiles) ? patientProfiles : [];
  const nameById = new Map(
    profiles.map((profile) => [profile.id, patientLabel(profile)])
  );
  const sessionProjects = projects
    .filter((project) =>
      isWritableInSession(project, sessionId, associatedPatientProfileId)
    )
    .sort(byUpdatedAtDesc);
  const patientProjects = projects
    .filter(
      (project) => !isWritableInSession(project, sessionId, associatedPatientProfileId)
    )
    .sort(byUpdatedAtDesc);
  const hostPatientName = associatedPatientProfileId
    ? nameById.get(associatedPatientProfileId)
    : undefined;

  const renderProject = (project: ProjectListItem, showDate: boolean) => {
    const isSelected = project.project_id === currentProjectId;
    const mixedIds = project.associated_patient_profile_ids ?? [];
    const mixedNames =
      mixedIds.length > 1
        ? mixedIds.map((id) => nameById.get(id) ?? 'Unknown patient').join(', ')
        : null;
    const Title = isSelected ? TypographyP2Bold : TypographyP2;

    return (
      <DropdownMenuItem
        key={project.project_id}
        onClick={() => onSelectProject(project.project_id)}
        selected={isSelected}
        data-testid={`session-work-project-item-${project.project_id}`}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 items-center justify-between gap-3">
            <Title className="truncate">{project.title}</Title>
            {showDate ? <HistoryDate value={project.updated_at} /> : null}
          </div>
          {mixedNames ? (
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="With {names}"
                id="sUiBJEIvWZ"
                description="Scribe history: mixed A+B chat lists involved patients (R42)"
                values={{ names: mixedNames }}
              />
            </TypographyCaption>
          ) : null}
        </div>
      </DropdownMenuItem>
    );
  };

  const renderRoutineGroup = (group: (typeof routineGroups)[number]) => {
    const selectableRun =
      group.runs.find((run) => run.project_id === currentProjectId) ??
      group.runs.find((run) => Boolean(run.project_id));
    if (!selectableRun?.project_id) return null;
    const isSelected = group.runs.some((run) => run.project_id === currentProjectId);
    const title =
      (selectableRun.source_workflow_id
        ? workflowNameById.get(selectableRun.source_workflow_id)
        : undefined) ??
      selectableRun.title?.trim() ??
      intl.formatMessage({ id: 'bCICQh++gs', defaultMessage: 'Routine run' });
    const Title = isSelected ? TypographyP2Bold : TypographyP2;

    return (
      <DropdownMenuItem
        key={group.key}
        onClick={() => onSelectProject(selectableRun.project_id)}
        selected={isSelected}
        data-testid={`session-work-routine-item-${group.key}`}
      >
        <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
          <Title className="truncate">{title}</Title>
          <div className="flex shrink-0 items-center gap-2">
            {group.runs.length > 1 ? (
              <TypographyCaption className="text-text-secondary">
                {intl.formatMessage(
                  { id: '9fnf7UqCFk', defaultMessage: '×{count}' },
                  { count: group.runs.length }
                )}
              </TypographyCaption>
            ) : null}
            {selectableRun.created_at ? (
              <HistoryDate value={selectableRun.created_at} />
            ) : null}
          </div>
        </div>
      </DropdownMenuItem>
    );
  };

  return (
    <>
      {isHistoryLoading && (
        <div className="flex items-center justify-center py-4">
          {/* The app's shared loader rendered in place — same wrapper, size, testid
              and classes — so nothing app-side has to cross. */}
          <div className="inline-flex shrink-0">
            <LoaderCircle
              size={16}
              strokeWidth={2}
              className="shrink-0 animate-spin text-text-secondary"
              data-testid="circle-loader"
            />
          </div>
        </div>
      )}

      {!isHistoryLoading && !hasProjects && (
        <div className="px-2 py-4 text-center">
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage
              defaultMessage="No other Heidi chats in this session yet"
              id="sVqZf+Z4AW"
              description="Session Work panel: project selector empty state"
            />
          </TypographyP2>
        </div>
      )}

      {!isHistoryLoading && hasProjects && (
        <>
          {routineGroups.length > 0 ? (
            <div className="flex flex-col gap-0.5">
              <TypographyCaption className="px-3 py-2.5 font-medium text-text-secondary">
                <FormattedMessage
                  defaultMessage="Routine runs"
                  id="27KjL2jgK5"
                  description="Scribe Work history section for routines run in this session"
                />
              </TypographyCaption>
              {routineGroups.map(renderRoutineGroup)}
            </div>
          ) : null}
          {sessionProjects.length > 0 && (
            <div
              className={
                routineGroups.length > 0
                  ? 'mt-1 flex flex-col gap-0.5 border-t border-border'
                  : 'flex flex-col gap-0.5'
              }
            >
              <TypographyCaption className="px-3 py-2.5 font-medium text-text-secondary">
                {hostPatientName ? (
                  <FormattedMessage
                    defaultMessage="From {name}'s sessions"
                    id="JErGno+cju"
                    description="Scribe history section for chats created from or using the open patient's sessions"
                    values={{ name: hostPatientName }}
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="From this session"
                    id="ZFKx2+pyGV"
                    description="Scribe history section for chats created from or using the open session"
                  />
                )}
              </TypographyCaption>
              {sessionProjects.map((project) => renderProject(project, true))}
            </div>
          )}
          {patientProjects.length > 0 && (
            <>
              {sessionProjects.length > 0 ? (
                <DropdownMenuSeparator className="m-0" />
              ) : null}
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <TypographyCaption className="font-medium text-text-secondary">
                    {hostPatientName ? (
                      <FormattedMessage
                        defaultMessage="With {name}"
                        id="rCHhHoW+Qj"
                        description="Scribe history section for chats associated with the session patient"
                        values={{ name: hostPatientName }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="With this session"
                        id="wjwHD2ew1d"
                        description="Scribe history section for read-only chats associated with an unlinked session"
                      />
                    )}
                  </TypographyCaption>
                  <Badge color="neutral" size="xs">
                    <FormattedMessage
                      defaultMessage="Read only"
                      id="TnujL1Kblq"
                      description="Scribe history badge for chats that can only be viewed in the session panel"
                    />
                  </Badge>
                </div>
                {patientProjects.map((project) => renderProject(project, false))}
              </div>
            </>
          )}
          {evidenceChats.length > 0 && onSelectEvidenceChat ? (
            <>
              {projects.length > 0 || routineGroups.length > 0 ? (
                <DropdownMenuSeparator className="m-0" />
              ) : null}
              <div className="flex flex-col gap-0.5">
                <TypographyCaption className="px-3 py-2.5 font-medium text-text-secondary">
                  <FormattedMessage
                    defaultMessage="Evidence chats"
                    id="j5qEfZf5QK"
                    description="Scribe history section for this session's Evidence-only chats"
                  />
                </TypographyCaption>
                {evidenceChats.map((chat) => {
                  const isSelected = chat.id === currentEvidenceChatId;
                  const Title = isSelected ? TypographyP2Bold : TypographyP2;
                  return (
                    <DropdownMenuItem
                      key={chat.id}
                      onClick={() => onSelectEvidenceChat(chat.id)}
                      selected={isSelected}
                      data-testid={`session-evidence-chat-item-${chat.id}`}
                    >
                      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                        <Title className="truncate">{chat.title}</Title>
                        <HistoryDate value={chat.updated_at} />
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </div>
            </>
          ) : null}
        </>
      )}
    </>
  );
};

/**
 * Dropdown for this session's Work history. Restricted rows stay writable in
 * the Scribe panel. Unrestricted / mixed rows are inspect-only here; Continue
 * opens them on Work home (R40–R43).
 */
export const SessionWorkProjectSelector = ({
  sessionId,
  associatedPatientProfileId,
  currentProjectId,
  currentProjectTitle,
  onSelectProject,
  evidenceChats = EMPTY_EVIDENCE_CHATS,
  currentEvidenceChatId = null,
  onSelectEvidenceChat,
  trigger,
  contentProps,
  onOpenChange,
  ...host
}: SessionWorkProjectSelectorProps & SessionWorkProjectSelectorHost) => {
  const trimmedTitle = currentProjectTitle?.trim();
  const [isHistoryWarm, setIsHistoryWarm] = useState(false);
  const warmHistory = () => setIsHistoryWarm(true);
  const {
    className: contentClassName,
    align = 'start',
    ...contentPlacementProps
  } = contentProps ?? {};

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      {isHistoryWarm ? (
        <SessionWorkHistoryPrefetch
          sessionId={sessionId}
          associatedPatientProfileId={associatedPatientProfileId}
          host={host}
        />
      ) : null}
      <DropdownMenuTrigger asChild onPointerEnter={warmHistory} onFocus={warmHistory}>
        {trigger ?? (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto w-max min-w-0 max-w-full shrink gap-1.5 overflow-hidden px-2 py-1"
            data-testid="session-work-project-selector"
          >
            <span
              className={cn(
                typographyVariant({ variant: 'p2Bold' }),
                'min-w-0 truncate text-start'
              )}
              title={trimmedTitle || undefined}
            >
              {trimmedTitle || (
                <FormattedMessage
                  defaultMessage="New chat"
                  id="5wbesnp0bZ"
                  description="Session Work panel: project selector trigger label with no active project"
                />
              )}
            </span>
            <ChevronDown size={14} className="shrink-0 text-text-secondary" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        {...contentPlacementProps}
        align={align}
        className={cn('w-[22rem] max-w-[calc(100vw-2rem)]', contentClassName)}
      >
        <SessionWorkProjectSelectorContent
          sessionId={sessionId}
          associatedPatientProfileId={associatedPatientProfileId}
          host={host}
          currentProjectId={currentProjectId}
          onSelectProject={onSelectProject}
          evidenceChats={evidenceChats}
          currentEvidenceChatId={currentEvidenceChatId}
          onSelectEvidenceChat={onSelectEvidenceChat}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
